import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMyUsage, listMyApiKeys } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CreateKeyModal } from "../components/CreateKeyModal";
import { KeyTable } from "../components/KeyTable";
import { RevealKeyModal } from "../components/RevealKeyModal";
import { UsageChart } from "../components/UsageChart";
import { UsageTable } from "../components/UsageTable";
import {
  DateRangePicker,
  defaultRange,
  rangeToParams,
  type DateRange,
  type Granularity,
} from "../components/DateRangePicker";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

export function DashboardRoute() {
  const { me } = useAuth();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(() => defaultRange(30));
  const [granularity, setGranularity] = useState<Granularity>("day");

  const keys = useQuery({
    queryKey: ["me", "api-keys"],
    queryFn: listMyApiKeys,
  });

  const usage = useQuery({
    queryKey: ["me", "usage", range, granularity],
    queryFn: () =>
      fetchMyUsage({ ...rangeToParams(range), granularity }),
  });

  if (!me) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Hallo {me.display_name}. Hier verwaltest du deine API-Keys und siehst
          deinen Verbrauch.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>API-Keys</CardTitle>
          <CreateKeyModal onCreated={(k) => setRevealed(k)} />
        </CardHeader>
        <CardContent>
          {keys.isLoading && <p className="text-sm">Lade…</p>}
          {keys.isError && (
            <p className="text-sm text-destructive">
              Konnte Keys nicht laden.
            </p>
          )}
          {keys.data && <KeyTable keys={keys.data} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verbrauch</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <DateRangePicker
            value={range}
            onChange={setRange}
            granularity={granularity}
            onGranularityChange={setGranularity}
          />
          {usage.isLoading && <p className="text-sm">Lade…</p>}
          {usage.isError && (
            <p className="text-sm text-destructive">
              Konnte Verbrauch nicht laden.
            </p>
          )}
          {usage.data && (
            <>
              <UsageChart
                buckets={usage.data.buckets}
                granularity={granularity}
              />
              <UsageTable
                buckets={usage.data.buckets}
                granularity={granularity}
              />
            </>
          )}
        </CardContent>
      </Card>

      <RevealKeyModal
        open={revealed !== null}
        apiKey={revealed}
        onClose={() => setRevealed(null)}
      />
    </div>
  );
}
